import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import toast from "react-hot-toast";
import { fetchRecoveryTradeHistory } from "../services/recoveryTradeApi";
import { useAuth } from "../context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import {
    FaSearch,
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaInfoCircle,
    FaHandHoldingUsd,
} from "react-icons/fa";

const PAGE_SIZE = 20;

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(num);
}

function formatDateTime(v) {
    if (!v) return "—";
    const normalized = String(v).includes("T") ? v : String(v).replace(" ", "T");
    const dt = new Date(normalized);
    if (!Number.isFinite(dt.getTime())) return safeText(v);
    return dt.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

function formatMobile(user) {
    const cc = safeText(user?.country_code);
    const mob = safeText(user?.mobile);
    if (cc === "—" && mob === "—") return "—";
    if (cc === "—") return mob;
    if (mob === "—") return cc;
    return `${cc} ${mob}`;
}

function isSessionExpired(out) {
    return typeof out?.error === "string" && out.error === "session expired";
}

function PlanBadge({ plan }) {
    const label = safeText(plan);
    if (label === "—") return <span className="text-muted">—</span>;
    return <span className="recovery-plan-badge">{label}</span>;
}

function LoadingRow({ cols = 9 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <div className="placeholder-glow">
                        <span className="placeholder col-12" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Math.max(1, Number(totalPages) || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Page {pageNo} of {totalPages}
            </div>
            <div className="d-flex align-items-center gap-1">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft />
                </button>
                <select
                    className="form-select form-select-sm mx-1"
                    style={{ width: "auto" }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

function UserCell({ user }) {
    const name = safeText(user?.name);
    if (name === "—") return <span className="text-muted">—</span>;
    return <div className="fw-semibold">{name}</div>;
}

function RecoveryDetailsModal({ open, item, onClose }) {
    if (!open || !item) return null;

    const u = item?.user || {};

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="modal-backdrop show d-block"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            position: "fixed",
                            inset: 0,
                            zIndex: 1040,
                        }}
                    />
                    <div
                        className="modal show d-block"
                        tabIndex="-1"
                        style={{ position: "fixed", inset: 0, zIndex: 1050, overflowY: "auto" }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="modal-dialog modal-dialog-centered modal-lg"
                            onClick={(e) => e.stopPropagation()}
                            style={{ maxWidth: "900px" }}
                        >
                            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                                <div className="modal-header border-bottom-0 p-3 recovery-modal-header">
                                    <h5 className="modal-title fw-bold mb-0 text-white">
                                        <FaHandHoldingUsd className="me-2" />
                                        Recovery Trade Details
                                    </h5>
                                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                                </div>

                                <div className="modal-body p-3">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="card border-0 bg-light">
                                                <div className="card-body p-3">
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Created</div>
                                                            <div className="fw-semibold mt-1">{formatDateTime(item?.create_date)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Modified</div>
                                                            <div className="fw-semibold mt-1">{formatDateTime(item?.modify_date)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-4">
                                                            <div className="text-muted small">Plan</div>
                                                            <div className="fw-semibold mt-1">{safeText(item?.plan)}</div>
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Amount</div>
                                                            <div className="fw-bold mt-1">{nfmt(item?.amount, 2)}</div>
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Pending</div>
                                                            <div className="fw-bold mt-1 text-warning">{nfmt(item?.pending_amount, 2)}</div>
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Claimed</div>
                                                            <div className="fw-bold mt-1 text-success">{nfmt(item?.claimed_amount, 2)}</div>
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <div className="text-muted small">Unclaimed</div>
                                                            <div className="fw-bold mt-1 text-danger">{nfmt(item?.unclaimed_amount, 2)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12">
                                            <div className="card border-0 bg-light">
                                                <div className="card-body p-3">
                                                    <h6 className="fw-bold text-primary mb-3">User</h6>
                                                    <div className="row g-3">
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Name</div>
                                                            <div className="fw-semibold mt-1">{safeText(u?.name)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Mobile</div>
                                                            <div className="fw-semibold mt-1">{formatMobile(u)}</div>
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <div className="text-muted small">Email</div>
                                                            <div className="fw-semibold mt-1 text-break">{safeText(u?.email)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer border-top-0 p-3" style={{ backgroundColor: "#f8f9fa" }}>
                                    <button className="btn btn-secondary px-3" type="button" onClick={onClose}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default function RecoveryTradeHistory() {
    const { logout } = useAuth();
    const [loading, setLoading] = useState(true);
    const [inputText, setInputText] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [pageNo, setPageNo] = useState(1);
    const [list, setList] = useState([]);
    const [meta, setMeta] = useState({ total: 0, limit: PAGE_SIZE, page_no: 1, total_page: 1 });
    const [selectedItem, setSelectedItem] = useState(null);

    const totalPages = useMemo(() => {
        const fromMeta = Number(meta?.total_page || 0);
        if (fromMeta > 0) return fromMeta;
        const total = Number(meta?.total || 0);
        const limit = Number(meta?.limit || PAGE_SIZE);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / limit));
    }, [meta?.total_page, meta?.total, meta?.limit]);

    const loadHistory = useCallback(
        async ({ page = 1, query = "" } = {}) => {
            const token = localStorage.getItem("token") || "";
            if (!token) {
                toast.error("Session missing. Please login again.");
                logout();
                return;
            }

            setLoading(true);
            try {
                const out = await fetchRecoveryTradeHistory({
                    token,
                    search: query,
                    page_no: page,
                    limit: PAGE_SIZE,
                });

                if (isSessionExpired(out)) {
                    toast.error("Session expired. Please login again.");
                    logout();
                    setList([]);
                    setMeta({ total: 0, limit: PAGE_SIZE, page_no: page, total_page: 1 });
                    return;
                }

                if (out?.error) {
                    toast.error(typeof out?.error === "string" ? out.error : "Failed to load recovery trade history");
                    setList([]);
                    setMeta({ total: 0, limit: PAGE_SIZE, page_no: page, total_page: 1 });
                    return;
                }

                const rows = Array.isArray(out?.data) ? out.data : [];
                const apiMeta = out?.meta || {};

                setList(rows);
                setMeta({
                    total: Number(apiMeta?.total || 0),
                    limit: Number(apiMeta?.limit || PAGE_SIZE),
                    page_no: Number(apiMeta?.page_no || page),
                    total_page: Number(apiMeta?.total_page || 1),
                });
            } catch (err) {
                console.error(err);
                toast.error("Failed to load recovery trade history");
                setList([]);
                setMeta({ total: 0, limit: PAGE_SIZE, page_no: page, total_page: 1 });
            } finally {
                setLoading(false);
            }
        },
        [logout]
    );

    useEffect(() => {
        loadHistory({ page: pageNo, query: appliedQuery });
    }, [pageNo, appliedQuery, loadHistory]);

    const onSearch = () => {
        setPageNo(1);
        setAppliedQuery(inputText.trim());
    };

    const onRefreshReset = async () => {
        setInputText("");
        setAppliedQuery("");
        setPageNo(1);
        await loadHistory({ page: 1, query: "" });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));
    const onRefresh = () => loadHistory({ page: pageNo, query: appliedQuery });

    return (
        <PageTransition>
            <div className="container-fluid py-3 recovery-history-page">
                <div className="recovery-hero mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h1 className="h3 mb-2 d-flex align-items-center gap-2 fw-bold">
                                <FaHandHoldingUsd />
                                Recovery Trade History
                            </h1>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="recovery-stat-pill">
                                    Total records: <strong>{nfmt(meta.total, 0)}</strong>
                                </span>
                                {appliedQuery ? (
                                    <span className="recovery-search-pill">
                                        Search: <strong>{appliedQuery}</strong>
                                    </span>
                                ) : null}
                                <span className="recovery-legend recovery-legend-pending">Pending</span>
                                <span className="recovery-legend recovery-legend-claimed">Claimed</span>
                                <span className="recovery-legend recovery-legend-unclaimed">Unclaimed</span>
                            </div>
                        </div>
                        <button className="btn btn-light recovery-refresh-btn" onClick={onRefresh} disabled={loading}>
                            <FaSyncAlt className={`me-2 ${loading ? "spin" : ""}`} />
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col">
                        <div className="card recovery-search-card border-0 shadow-sm">
                            <div className="card-body">
                                <p className="text-muted small mb-2">
                                    Search by name, email, mobile, user ID, country code, plan, or recovery ID
                                </p>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <div className="input-group" style={{ maxWidth: 420 }}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search recovery trades..."
                                            value={inputText}
                                            onChange={(e) => setInputText(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && onSearch()}
                                            disabled={loading}
                                        />
                                        <button className="btn btn-primary" type="button" onClick={onSearch} disabled={loading}>
                                            <FaSearch />
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-secondary btn-sm" type="button" onClick={onRefreshReset} disabled={loading}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <div className="card recovery-table-card border-0 shadow-sm">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 recovery-history-table">
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>Date</th>
                                                <th>Plan</th>
                                                <th>Amount</th>
                                                <th>Pending</th>
                                                <th>Claimed</th>
                                                <th>Unclaimed</th>
                                                <th>User</th>
                                                <th className="text-center">
                                                    <FaInfoCircle size={12} title="Info" />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                [...Array(5)].map((_, i) => <LoadingRow key={i} cols={9} />)
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <FaSearch className="mb-2" size={24} />
                                                            <p className="mb-0">No recovery trade history found</p>
                                                            {appliedQuery ? <small>Try changing your search query</small> : null}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((row, idx) => (
                                                    <tr key={`${row?.recovery_id || ""}_${idx}`}>
                                                        <td className="fw-semibold">
                                                            {(pageNo - 1) * Number(meta?.limit || PAGE_SIZE) + idx + 1}
                                                        </td>
                                                        <td>{formatDateTime(row?.create_date)}</td>
                                                        <td><PlanBadge plan={row?.plan} /></td>
                                                        <td className="fw-semibold recovery-amount-total">{nfmt(row?.amount, 2)}</td>
                                                        <td><span className="recovery-amount-pending">{nfmt(row?.pending_amount, 2)}</span></td>
                                                        <td><span className="recovery-amount-claimed">{nfmt(row?.claimed_amount, 2)}</span></td>
                                                        <td><span className="recovery-amount-unclaimed">{nfmt(row?.unclaimed_amount, 2)}</span></td>
                                                        <td>
                                                            <UserCell user={row?.user} />
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-warning px-1 py-0 recovery-info-btn"
                                                                onClick={() => setSelectedItem(row)}
                                                                title="View details"
                                                            >
                                                                <FaInfoCircle size={12} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && list.length > 0 && (
                                    <div className="card-footer">
                                        <PaginationBar
                                            loading={loading}
                                            pageNo={pageNo}
                                            totalPages={totalPages}
                                            onPrev={onPrev}
                                            onNext={onNext}
                                            onJump={onJump}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <RecoveryDetailsModal open={!!selectedItem} item={selectedItem} onClose={() => setSelectedItem(null)} />

                <style jsx>{`
                    .recovery-history-page {
                        --recovery-purple: #6f42c1;
                        --recovery-orange: #fd7e14;
                    }
                    .recovery-hero {
                        border-radius: 16px;
                        padding: 1.25rem 1.5rem;
                        background: linear-gradient(135deg, rgba(111, 66, 193, 0.14), rgba(253, 126, 20, 0.12));
                        border: 1px solid rgba(111, 66, 193, 0.2);
                    }
                    .recovery-stat-pill,
                    .recovery-search-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.35rem;
                        padding: 0.35rem 0.75rem;
                        border-radius: 999px;
                        background: rgba(255, 255, 255, 0.88);
                        font-size: 0.875rem;
                    }
                    .recovery-search-pill {
                        background: rgba(255, 243, 205, 0.95);
                        border: 1px solid rgba(255, 193, 7, 0.35);
                    }
                    .recovery-legend {
                        display: inline-flex;
                        padding: 0.3rem 0.65rem;
                        border-radius: 999px;
                        font-size: 0.75rem;
                        font-weight: 700;
                    }
                    .recovery-legend-pending {
                        color: #997404;
                        background: rgba(255, 193, 7, 0.18);
                        border: 1px solid rgba(255, 193, 7, 0.35);
                    }
                    .recovery-legend-claimed {
                        color: #146c43;
                        background: rgba(25, 135, 84, 0.14);
                        border: 1px solid rgba(25, 135, 84, 0.3);
                    }
                    .recovery-legend-unclaimed {
                        color: #b02a37;
                        background: rgba(220, 53, 69, 0.12);
                        border: 1px solid rgba(220, 53, 69, 0.3);
                    }
                    .recovery-refresh-btn {
                        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
                    }
                    .recovery-search-card {
                        border-radius: 14px;
                        border-left: 4px solid var(--recovery-purple) !important;
                    }
                    .recovery-table-card {
                        border-radius: 14px;
                        overflow: hidden;
                    }
                    .recovery-history-table thead {
                        background: linear-gradient(90deg, #6f42c1, #fd7e14);
                        color: #fff;
                    }
                    .recovery-history-table thead th {
                        font-weight: 600;
                        font-size: 0.75rem;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                        white-space: nowrap;
                        border: none;
                        padding: 12px;
                    }
                    .recovery-history-table tbody tr:hover {
                        background: rgba(111, 66, 193, 0.04);
                    }
                    .recovery-history-table td {
                        vertical-align: middle;
                        font-size: 0.9rem;
                    }
                    .recovery-plan-badge {
                        display: inline-flex;
                        padding: 0.3rem 0.65rem;
                        border-radius: 999px;
                        font-size: 0.78rem;
                        font-weight: 700;
                        color: #5a32a3;
                        background: rgba(111, 66, 193, 0.12);
                        border: 1px solid rgba(111, 66, 193, 0.25);
                    }
                    .recovery-amount-total {
                        color: #0d6efd;
                    }
                    .recovery-amount-pending {
                        color: #cc9a06;
                        font-weight: 700;
                    }
                    .recovery-amount-claimed {
                        color: #198754;
                        font-weight: 700;
                    }
                    .recovery-amount-unclaimed {
                        color: #dc3545;
                        font-weight: 700;
                    }
                    .recovery-modal-header {
                        background: linear-gradient(135deg, #6f42c1, #fd7e14);
                    }
                    .recovery-info-btn:hover {
                        background: #fd7e14;
                        border-color: #fd7e14;
                        color: #fff;
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}</style>
            </div>
        </PageTransition>
    );
}
